import { useSyncExternalStore } from 'react';
import type { IconName } from '../components/icons';

/**
 * Darstellung (U11): System, Hell oder Dunkel. Die Wahl liegt je Gerät im lokalen Speicher; „System“ ist der Standard und
 * entfernt das Attribut `data-theme` am `<html>`, dann folgen die Tokens der Systemeinstellung. Jeder Zugriff auf den
 * Speicher ist abgesichert (privates Fenster, gesperrt), die Seite darf daran nie scheitern.
 */
export type ThemeChoice = 'system' | 'light' | 'dark';

export const THEME_KEY = 'barso.theme';
export const THEME_CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark'];
/** Symbol je Wahl (rein dekorativ, der Text steht daneben). */
export const THEME_ICONS: Record<ThemeChoice, IconName> = {
  system: 'monitor',
  light: 'sun',
  dark: 'moon',
};
export const THEME_LABELS: Record<ThemeChoice, string> = {
  system: 'System',
  light: 'Hell',
  dark: 'Dunkel',
};

/** Farbe der Browser-/Statusleiste (Kartenfläche der Tokens, neutral). */
const THEME_COLOR = { light: '#FFFFFF', dark: '#1D1D1D' } as const;

/** Beobachter der Wahl (Konto-Menü, Einstellungen): beide zeigen immer den aktuellen Stand. */
const listeners = new Set<() => void>();

/** Meldet sich für Änderungen der Wahl an; gibt die Abmeldung zurück. */
export function subscribeTheme(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

/** Wahl, die sich nicht speichern ließ: gilt bis zum Neuladen. */
let unsaved: ThemeChoice | null = null;

export function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === 'system' || value === 'light' || value === 'dark';
}

/** Gespeicherte Wahl, sonst „System“. */
export function readTheme(): ThemeChoice {
  if (unsaved !== null) return unsaved;
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return isThemeChoice(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/** Setzt `data-theme`, `color-scheme` und `theme-color` am Dokument (ohne Speichern). */
export function applyTheme(choice: ThemeChoice, doc: Document = document): void {
  const root = doc.documentElement;
  const system =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;
  if (choice === 'system') {
    root.removeAttribute('data-theme');
    root.style.removeProperty('color-scheme');
  } else {
    root.setAttribute('data-theme', choice);
    root.style.setProperty('color-scheme', choice);
  }
  const dark = choice === 'dark' || (choice === 'system' && system);
  doc
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', dark ? THEME_COLOR.dark : THEME_COLOR.light);
}

/** Merkt die Wahl und wendet sie an. Gelingt das Speichern nicht, gilt sie bis zum Neuladen. */
export function setTheme(choice: ThemeChoice): void {
  try {
    if (choice === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, choice);
    unsaved = null;
  } catch {
    unsaved = choice;
  }
  applyTheme(choice);
  for (const listener of [...listeners]) listener();
}

/** Aktuelle Wahl für React; folgt Änderungen von außen (anderer Baustein, anderer Tab). */
export function useThemeChoice(): ThemeChoice {
  return useSyncExternalStore(subscribeTheme, readTheme, () => 'system');
}

/** Beim Start vor dem ersten Rendern aufrufen (main.tsx). */
export function initTheme(): void {
  applyTheme(readTheme());
  if (typeof window.matchMedia !== 'function') return;
  // Bei „System“ folgt auch die Statusleistenfarbe späteren Wechseln des Geräts
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (readTheme() === 'system') applyTheme('system');
  });
  // Anderer Tab hat gewählt: übernehmen und die Anzeige nachziehen
  window.addEventListener('storage', (event) => {
    if (event.key !== THEME_KEY && event.key !== null) return;
    applyTheme(readTheme());
    for (const listener of [...listeners]) listener();
  });
}
